import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useRevokeQrCode } from "@/features/merchant/api/use-revoke-qr-code";
import type { QrCodeDto } from "@/features/merchant/api/dto";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";
import {
  createRevokeQrCodeSchema,
  type RevokeQrCodeFormValues,
} from "@/features/merchant/schemas/qr-code.schema";

/**
 * Dialog de révocation d'un QR code — motif obligatoire (query param
 * `reason`, guide §6.6).
 */
export function QrCodeRevokeDialog({
  qr,
  open,
  onOpenChange,
  merchantId,
  actingMerchantId,
}: {
  qr: QrCodeDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string;
  actingMerchantId?: string;
}) {
  const { t } = useTypedTranslation();
  const { t: tm } = useMerchantTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue.
  const schema = useMemo(() => createRevokeQrCodeSchema(), [i18n.language]);

  const revoke = useRevokeQrCode(merchantId);

  const form = useForm({
    defaultValues: { reason: "" } as RevokeQrCodeFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success || !qr?.id) return;
      revoke.mutate(
        { qrCodeId: qr.id, actingMerchantId, reason: parsed.data.reason.trim() },
        {
          onSuccess: () => {
            notifSilence(tm("merchant.qrCodes.feedback.revoked"));
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        }
      );
    },
  });

  useEffect(() => {
    if (open) form.reset({ reason: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, qr?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{tm("merchant.qrCodes.revoke.title")}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="reason">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="qr-revoke-reason">
                  {tm("merchant.qrCodes.revoke.reason")}
                </Label>
                <Textarea
                  id="qr-revoke-reason"
                  rows={3}
                  value={field.state.value}
                  placeholder={tm("merchant.qrCodes.revoke.reasonPlaceholder")}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={revoke.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={revoke.isPending}>
              {revoke.isPending ? <ButtonSpinner /> : null}
              {tm("merchant.qrCodes.revoke.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
